import React, { Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Layout from '../Layout/Layout';
import ChunkErrorBoundary from './ChunkErrorBoundary';
import Home from '../../pages/Home/Home';
import { EASE, duration } from '../../lib/motion';

// Secondary routes are code-split: each loads as its own chunk on first
// visit, so the main bundle stays lean (the gallery especially — its
// lightbox/masonry machinery never taxes the marketing pages). Home stays
// EAGER on purpose: it is the primary cold entry, and splitting it only
// trades a serial round-trip on the landing page for ~12KB of main bundle.
const Privacy = React.lazy(() => import('../../pages/Privacy/Privacy'));
const Terms = React.lazy(() => import('../../pages/Terms/Terms'));
const DeleteAccount = React.lazy(
  () => import('../../pages/DeleteAccount/DeleteAccount'),
);
const CopyrightPolicy = React.lazy(
  () => import('../../pages/legal/CopyrightPolicy'),
);
const GalleryVisitorNotice = React.lazy(
  () => import('../../pages/legal/GalleryVisitorNotice'),
);
const LegalNotice = React.lazy(() => import('../../pages/legal/LegalNotice'));
const Security = React.lazy(() => import('../../pages/legal/Security'));
const Accessibility = React.lazy(
  () => import('../../pages/legal/Accessibility'),
);
const LegalHub = React.lazy(() => import('../../pages/legal/LegalHub'));
const Guidelines = React.lazy(
  () => import('../../pages/Guidelines/Guidelines'),
);
const Guide = React.lazy(() => import('../../pages/Guide/Guide'));
const Tutorials = React.lazy(() => import('../../pages/Tutorials/Tutorials'));
const galleryImport = () => import('../../pages/Gallery/Gallery');
const Gallery = React.lazy(galleryImport);
const NotFound = React.lazy(() => import('../../pages/NotFound/NotFound'));
const PosePage = React.lazy(() => import('../../pages/Share/PosePage'));
const CollectionPage = React.lazy(
  () => import('../../pages/Share/CollectionPage'),
);
const ProfilePage = React.lazy(() => import('../../pages/Share/ProfilePage'));
const BookingPage = React.lazy(() => import('../../pages/Share/BookingPage'));

// A delivered gallery link is a COLD entry — without this, the (largest)
// gallery chunk would only start fetching after the main bundle executes
// and the route renders, a fully serial second round-trip on the one page
// clients open from a text message. Kick the fetch off at module scope so
// it overlaps main-bundle execution.
if (/^\/gallery\//.test(window.location.pathname)) {
  galleryImport();
}

/**
 * Opacity-only page wrapper: quiet crossfade between routes. Pages
 * bring their own entrance choreography (the gallery especially), so
 * the route layer stays restrained.
 */
const Page: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: duration.fast + 0.05, ease: EASE }}
  >
    {children}
  </motion.div>
);

/**
 * Suspense boundary for a lazy page, nested INSIDE the Page crossfade
 * (and inside the Layout chrome where there is one), wrapped in the
 * chunk error boundary: the transition covers a fast chunk load, the
 * fallback keeps the chrome from collapsing on a slow one, and a failed
 * load reloads once (deploy skew) or shows a retry screen instead of
 * white-screening the root. The boundary sits inside Page so an exit
 * animation always plays on the already-loaded outgoing tree.
 *
 * The fallback reserves page height rather than showing a spinner — the
 * footer must not flash up to the header and then pop away when the
 * chunk lands a beat after the crossfade.
 */
const Load: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ChunkErrorBoundary>
    <Suspense fallback={<div style={{ minHeight: '70vh' }} aria-hidden />}>
      {children}
    </Suspense>
  </ChunkErrorBoundary>
);

/**
 * Chromeless variant for the client gallery: a quiet ink screen (the
 * gallery's own film-gate opens from near-black, so this reads as the
 * room going dark rather than a loading state) with the wordmark held
 * faint at center. Shown only on cold entries slow enough to outlive
 * the entrance fade.
 */
const galleryFallback = (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--surface-bg)',
    }}
    aria-label="Loading gallery"
  >
    <span
      style={{
        fontSize: '0.8rem',
        letterSpacing: '0.45em',
        textTransform: 'uppercase',
        color: 'var(--text-muted)',
      }}
    >
      Muse
    </span>
  </div>
);

const LoadGallery: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <ChunkErrorBoundary>
    <Suspense fallback={galleryFallback}>{children}</Suspense>
  </ChunkErrorBoundary>
);

const AnimatedRoutes: React.FC = () => {
  const location = useLocation();

  return (
    <AnimatePresence
      mode="wait"
      onExitComplete={() => {
        // Reset scroll while the screen is between pages; 'instant'
        // bypasses the global `scroll-behavior: smooth`.
        window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
      }}
    >
      <Routes location={location} key={location.pathname}>
        {/* Marketing pages get the site chrome (header/footer). Home is
            eager (see imports) so the landing page needs no boundary. */}
        <Route
          path="/"
          element={
            <Page>
              <Layout>
                <Home />
              </Layout>
            </Page>
          }
        />
        <Route
          path="/privacy"
          element={
            <Page>
              <Layout>
                <Load>
                  <Privacy />
                </Load>
              </Layout>
            </Page>
          }
        />
        {/* Legal + help pages the app links to (Terms/Privacy screen and the
            Help Center). Each is its own code-split chunk. */}
        <Route
          path="/terms"
          element={
            <Page>
              <Layout>
                <Load>
                  <Terms />
                </Load>
              </Layout>
            </Page>
          }
        />
        <Route
          path="/delete-account"
          element={
            <Page>
              <Layout>
                <Load>
                  <DeleteAccount />
                </Load>
              </Layout>
            </Page>
          }
        />
        <Route
          path="/copyright"
          element={
            <Page>
              <Layout>
                <Load>
                  <CopyrightPolicy />
                </Load>
              </Layout>
            </Page>
          }
        />
        <Route
          path="/gallery-notice"
          element={
            <Page>
              <Layout>
                <Load>
                  <GalleryVisitorNotice />
                </Load>
              </Layout>
            </Page>
          }
        />
        <Route
          path="/legal-notice"
          element={
            <Page>
              <Layout>
                <Load>
                  <LegalNotice />
                </Load>
              </Layout>
            </Page>
          }
        />
        <Route
          path="/security"
          element={
            <Page>
              <Layout>
                <Load>
                  <Security />
                </Load>
              </Layout>
            </Page>
          }
        />
        <Route
          path="/accessibility"
          element={
            <Page>
              <Layout>
                <Load>
                  <Accessibility />
                </Load>
              </Layout>
            </Page>
          }
        />
        <Route
          path="/legal"
          element={
            <Page>
              <Layout>
                <Load>
                  <LegalHub />
                </Load>
              </Layout>
            </Page>
          }
        />
        <Route
          path="/guidelines"
          element={
            <Page>
              <Layout>
                <Load>
                  <Guidelines />
                </Load>
              </Layout>
            </Page>
          }
        />
        <Route
          path="/guide"
          element={
            <Page>
              <Layout>
                <Load>
                  <Guide />
                </Load>
              </Layout>
            </Page>
          }
        />
        <Route
          path="/tutorials"
          element={
            <Page>
              <Layout>
                <Load>
                  <Tutorials />
                </Load>
              </Layout>
            </Page>
          }
        />
        {/* Share landing pages — the browser fallback for app share
            links (bots get the serverless OG cards via vercel.json) */}
        <Route
          path="/pose/:id"
          element={
            <Page>
              <Layout>
                <Load>
                  <PosePage />
                </Load>
              </Layout>
            </Page>
          }
        />
        <Route
          path="/collection/:id"
          element={
            <Page>
              <Layout>
                <Load>
                  <CollectionPage />
                </Load>
              </Layout>
            </Page>
          }
        />
        <Route
          path="/profile/:id"
          element={
            <Page>
              <Layout>
                <Load>
                  <ProfilePage />
                </Load>
              </Layout>
            </Page>
          }
        />
        {/* Calendar-event hand-off — private, renders no booking data */}
        <Route
          path="/booking/:id"
          element={
            <Page>
              <Layout>
                <Load>
                  <BookingPage />
                </Load>
              </Layout>
            </Page>
          }
        />
        {/* Client galleries are chromeless — the photos are the brand */}
        <Route
          path="/gallery/:token"
          element={
            <Page>
              <LoadGallery>
                <Gallery />
              </LoadGallery>
            </Page>
          }
        />
        <Route
          path="*"
          element={
            <Page>
              <Layout>
                <Load>
                  <NotFound />
                </Load>
              </Layout>
            </Page>
          }
        />
      </Routes>
    </AnimatePresence>
  );
};

export default AnimatedRoutes;
